import { faExchange, faSignOut } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { AssignmentServiceRoot } from 'src/apis/assignment-service/types';
import type { AuthorizationServiceRoot } from 'src/apis/authorization-service/types';
import type { CapacityManagementRootResponse } from 'src/apis/capacity-management/types';
import type { DirectFcfsEntrypoint } from 'src/apis/direct-fcfs/types';
import type { HydrogenFcfsRoot } from 'src/apis/hydrogen-fcfs/types';
import type { OrganisationServiceRoot } from 'src/apis/organisation-service/types';
import type { RegularFcfsRoot } from 'src/apis/regular-fcfs/types';
import type { RemitReportingEntrypoint } from 'src/apis/remit-reporting/types';
import type { SurrenderRootResponse } from 'src/apis/surrender-service/types';
import { useActionMode } from 'src/components/action-mode';
import { Button } from 'src/components/buttons-and-actions/button';
import { Link } from 'src/components/navigation/link';
import type { AccountsContextType } from 'src/hooks/use-accounts';
import { useAccounts } from 'src/hooks/use-accounts';
import type { EnhancedAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { cn } from 'src/utils/cn';
import { isLng } from 'src/utils/is-lng';
import { setLocationHref } from 'src/utils/location-usage';

type UserMenuSection =
  | 'personalSettings'
  | 'companySettings'
  | 'transportSettings'
  | 'shipperSettings'
  | 'storageSettings'
  | 'otherSettings'
  | 'reportingSettings';

export type UserMenuProps = {
  // treat the services as optional to be more resilient in the future
  // (e.g. assume we are authenticated with Cognito, but a service is offline)
  authorizationService: AuthorizationServiceRoot | null;
  organisationService: OrganisationServiceRoot | null;
  capacityManagementService: CapacityManagementRootResponse | null;
  regularFcfsService?: RegularFcfsRoot;
  directFcfsService?: DirectFcfsEntrypoint;
  hydrogenFcfsRoot?: HydrogenFcfsRoot;
  show: Record<UserMenuSection, boolean>;
  assignmentService?: AssignmentServiceRoot | null;
  remitReportingService?: RemitReportingEntrypoint;
  surrenderServiceRoot?: SurrenderRootResponse;
};

export const UserMenu: FC<UserMenuProps> = ({
  authorizationService,
  organisationService,
  capacityManagementService,
  regularFcfsService,
  directFcfsService,
  hydrogenFcfsRoot,
  show,
  assignmentService,
  remitReportingService,
  surrenderServiceRoot,
}) => {
  const accounts = useAccounts();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const mode = useMenuMode();

  const {
    personalSettings,
    companySettings,
    transportSettings,
    storageSettings,
    shipperSettings,
    otherSettings,
    reportingSettings,
  } = useMenuSections({
    authorizationService,
    organisationService,
    capacityManagementService,
    regularFcfsService,
    directFcfsService,
    hydrogenFcfsRoot,
    monolithUser,
    assignmentService,
    remitReportingService,
    surrenderServiceRoot,
    accounts,
    mode,
  });

  return (
    <div
      className={cn(
        mode === 'sidebar-item' &&
          `
            grid w-[20rem] gap-2xl
            legacy:gap-[2rem]
          `,
        mode === 'dropdown' &&
          `
            grid max-w-[26rem] overflow-auto rounded-xs
            legacy:my-[-0.9rem]
          `
      )}
    >
      {show.personalSettings && show.otherSettings && accounts.current && (
        <div
          className={`
            grid items-center bg-legacy-background
            not-md:border-b not-md:border-b-legacy-brand-light-1
            not-md:bg-transparent not-md:text-white
            legacy:gap-[1rem] legacy:pb-[2rem]
            legacy:not-md:m-[2rem_0]
            legacy:md:p-[1.2rem_2rem]
          `}
          data-testid="account-info"
        >
          <div className="grid items-center">
            {accounts.current.organisationName && (
              <div>
                <strong>{accounts.current.organisationName}</strong>
              </div>
            )}

            <span
              className={`
                text-legacy-xs text-legacy-brand-light-2
                md:text-legacy-brand-light-1
              `}
            >
              {accounts.current.email}
            </span>
          </div>

          <Link
            to="/login/accounts"
            mode="button-secondary"
            size="small"
            className="legacy:text-center"
            data-testid="switch-account-link"
          >
            Switch Account
          </Link>
        </div>
      )}

      {show.personalSettings && (
        <MenuSection
          mode={mode}
          section="personalSettings"
          title="Personal Settings"
          settings={personalSettings}
        />
      )}

      {show.companySettings && (
        <MenuSection
          mode={mode}
          section="companySettings"
          title="Company Settings"
          settings={companySettings}
        />
      )}

      {show.transportSettings && (
        <MenuSection
          mode={mode}
          section="transportSettings"
          title="Transport Settings"
          settings={transportSettings}
        />
      )}

      {show.storageSettings && (
        <MenuSection
          mode={mode}
          section="storageSettings"
          title={isLng(monolithUser) ? 'LNG Settings' : 'Storage Settings'}
          settings={storageSettings}
        />
      )}

      {show.reportingSettings && (
        <MenuSection
          mode={mode}
          section="reportingSettings"
          title="Reporting Settings"
          settings={reportingSettings}
        />
      )}

      {show.shipperSettings && (
        <MenuSection
          mode={mode}
          section="shipperSettings"
          title="Shipper Settings"
          settings={shipperSettings}
        />
      )}

      {show.otherSettings && (
        <MenuSection
          mode={mode}
          section="otherSettings"
          title={null}
          settings={otherSettings}
        />
      )}
    </div>
  );
};

type UserMenuMode = 'link-primary' | 'dropdown' | 'sidebar-item';

function useMenuMode(): UserMenuMode {
  const actionMode = useActionMode();
  if (!actionMode)
    throw new Error(
      '<UserMenu/> needs to be wrapped in <ActionModeProvider/>.'
    );

  if (
    actionMode.mode !== 'link-primary' &&
    actionMode.mode !== 'dropdown' &&
    actionMode.mode !== 'sidebar-item'
  )
    throw new Error(
      `<UserMenu/> was placed inside <ActionModeProvider value="${actionMode.mode}"/>. This mode is not supported.`
    );

  return actionMode.mode;
}

type MenuSectionsData = {
  personalSettings: ReactNode[];
  companySettings: ReactNode[];
  transportSettings: ReactNode[];
  shipperSettings: ReactNode[];
  storageSettings: ReactNode[];
  otherSettings: ReactNode[];
  reportingSettings: ReactNode[];
};

const useMenuSections = ({
  authorizationService,
  organisationService,
  capacityManagementService,
  regularFcfsService,
  directFcfsService,
  hydrogenFcfsRoot,
  monolithUser,
  assignmentService,
  remitReportingService,
  surrenderServiceRoot,
  accounts,
  mode,
}: {
  authorizationService: AuthorizationServiceRoot | null;
  organisationService: OrganisationServiceRoot | null;
  capacityManagementService: CapacityManagementRootResponse | null;
  regularFcfsService?: RegularFcfsRoot;
  directFcfsService?: DirectFcfsEntrypoint;
  hydrogenFcfsRoot?: HydrogenFcfsRoot;
  monolithUser: EnhancedAuthenticatedMonolithUser | null;
  assignmentService?: AssignmentServiceRoot | null;
  remitReportingService?: RemitReportingEntrypoint;
  surrenderServiceRoot?: SurrenderRootResponse;
  accounts: AccountsContextType;
  mode: UserMenuMode;
}): MenuSectionsData => {
  const safeLocalStorage = useSafeLocalStorage();
  const renderDirectFcfsLink =
    !!directFcfsService?._links?.['read-configuration'];
  const renderRegularFcfsLink =
    !!regularFcfsService?._links?.['operator-settings'];
  const renderHydrogenFcfsLink =
    !!hydrogenFcfsRoot?._links?.organisationFcfsSettings;
  const renderRefactoredSurrenderSettingsLink =
    !!surrenderServiceRoot?._links.isSurrenderForAllEnabled;
  const { pathname, hash } = useLocation();

  const personalSettings: ReactNode[] = [];
  const companySettings: ReactNode[] = [];
  const shipperSettings: ReactNode[] = [];
  const transportSettings: ReactNode[] = [];
  const storageSettings: ReactNode[] = [];
  const otherSettings: ReactNode[] = [];
  const reportingSettings: ReactNode[] = [];

  personalSettings.push(
    <Link
      to="/settings/profile"
      data-help-annotation={mode === 'dropdown' ? 'profile:2' : undefined}
    >
      Profile
    </Link>
  );

  personalSettings.push(
    <Link to="/settings/notifications">Notification Settings</Link>
  );

  if (organisationService?._links.getUserDetails) {
    companySettings.push(
      <Link
        to="/settings/organisation/users"
        data-testid="user-management-link"
      >
        User Management
      </Link>
    );
  }

  if (authorizationService?._links.permissionGroups) {
    companySettings.push(
      <Link to="/settings/permission-groups">Permission Groups</Link>
    );
  }

  if (organisationService?._links.getMyOrganisationDetails) {
    companySettings.push(
      <Link
        isActive={pathname.startsWith(
          '/settings/organisation/company-information'
        )}
        to="/settings/organisation/company-information"
      >
        Company Information
      </Link>
    );
  }

  if (capacityManagementService?._links?.settingsInvoicing) {
    companySettings.push(
      <Link
        isActive={pathname.startsWith('/settings/organisation/invoices')}
        to="/settings/organisation/invoices"
      >
        Invoice Settings
      </Link>
    );
  }

  if (organisationService?._links.contacts) {
    companySettings.push(
      <Link
        to="/settings/organisation/contacts"
        isActive={pathname.startsWith('/settings/organisation/contacts')}
      >
        Contacts
      </Link>
    );
  }

  if (
    monolithUser?.role === 'SHIPPER_ADMIN' ||
    remitReportingService?._links?.['secondary-settings-details']
  ) {
    companySettings.push(
      <Link
        to="/settings/premium-services"
        data-help-annotation="shipper-api:2"
      >
        Premium Services
      </Link>
    );
  }

  if (monolithUser?.role === 'SHIPPER_ADMIN') {
    reportingSettings.push(
      <Link to="/settings/reporting/standard-reports">Standard Reports</Link>
    );
  }

  if (
    remitReportingService?._links?.['exclude-network-points'] ||
    remitReportingService?._links?.['primary-settings-details']
  ) {
    reportingSettings.push(
      <Link to="/settings/remit-reporting">REMIT Reporting</Link>
    );
  }

  if (monolithUser?.isShipper) {
    personalSettings.push(
      <Link
        isActive={
          pathname === '/settings/platform/' &&
          hash === '#/user-settings/general'
        }
        to="/settings/platform/#/user-settings/general"
      >
        View
      </Link>
    );
    personalSettings.push(
      <Link
        isActive={
          pathname === '/settings/platform/' &&
          hash === '#/user-settings/dashboard'
        }
        to="/settings/platform/#/user-settings/dashboard"
      >
        Dashboard
      </Link>
    );
    personalSettings.push(
      <Link
        isActive={
          pathname === '/settings/platform/' &&
          hash === '#/user-settings/network-points'
        }
        to="/settings/platform/#/user-settings/network-points"
      >
        Network Points
      </Link>
    );

    personalSettings.push(
      <Link to="/settings/integration-client-auth">Booking Authorisation</Link>
    );

    transportSettings.push(
      <Link
        isActive={
          pathname === '/settings/platform/' &&
          (hash === '#/user-settings/secondary-trading' ||
            hash === '#/user-settings/new-trader-list')
        }
        to="/settings/platform/#/user-settings/secondary-trading"
      >
        Secondary Trading
      </Link>
    );
  }

  if (monolithUser?.isTso) {
    transportSettings.push(
      <Link to="/settings/general-transport-settings">General</Link>
    );

    transportSettings.push(<Link to="/settings/auctions">Auctions</Link>);

    transportSettings.push(
      <Link
        to="/settings/platform/#/operator-settings/secondary-trading"
        isActive={
          pathname === '/settings/platform/' &&
          hash === '#/operator-settings/secondary-trading'
        }
      >
        Secondary Trading
      </Link>
    );

    transportSettings.push(
      renderRefactoredSurrenderSettingsLink ? (
        <Link to="/settings/surrender">Surrender</Link>
      ) : (
        <Link
          to="/settings/platform/#/operator-settings/surrender"
          isActive={
            pathname === '/settings/platform/' &&
            hash === '#/operator-settings/surrender'
          }
        >
          Surrender
        </Link>
      )
    );
  }

  if (renderRegularFcfsLink) {
    transportSettings.push(
      <Link to="/settings/regular-fcfs">Regular FCFS</Link>
    );
  }

  if (renderDirectFcfsLink) {
    transportSettings.push(<Link to="/settings/direct-fcfs">Direct FCFS</Link>);
  }

  if (renderHydrogenFcfsLink) {
    transportSettings.push(
      <Link to="/settings/hydrogen-fcfs">Hydrogen FCFS</Link>
    );
  }

  if (monolithUser?.isSso) {
    companySettings.push(
      <Link
        to={
          isLng(monolithUser)
            ? '/settings/platform/#/lng-settings/premium-services'
            : '/settings/platform/#/storage-settings/premium-services'
        }
        isActive={
          pathname === '/settings/platform/' &&
          (isLng(monolithUser)
            ? hash === '#/lng-settings/premium-services'
            : hash === '#/storage-settings/premium-services')
        }
      >
        Premium Services
      </Link>
    );

    storageSettings.push(
      <Link
        to={
          isLng(monolithUser)
            ? '/settings/platform/#/lng-settings/general'
            : '/settings/platform/#/storage-settings/general'
        }
        isActive={
          pathname === '/settings/platform/' &&
          (isLng(monolithUser)
            ? hash === '#/lng-settings/general'
            : hash === '#/storage-settings/general')
        }
      >
        General
      </Link>
    );
  }

  if (assignmentService?._links.assignmentSettings) {
    shipperSettings.push(
      <Link to="/settings/assignment-settings">Assignment Settings</Link>
    );
  }
  if (monolithUser?.isTso) {
    shipperSettings.push(
      <Link
        to="/settings/platform/#/operator-settings/credit-limits"
        isActive={
          pathname === '/settings/platform/' &&
          hash === '#/operator-settings/credit-limits'
        }
      >
        Credit Limits
      </Link>
    );
  }

  if (
    monolithUser?.canSwitchToOrganisationRole === 'TSO' ||
    monolithUser?.canSwitchToOrganisationRole === 'SSO'
  ) {
    otherSettings.push(
      <Button
        onClick={() => {
          safeLocalStorage.removeItem('ENFORCE_SHIPPER_ROLE');
          setLocationHref(PRISMA_CONFIG.angularUrl); // hard reload and redirect to start page
        }}
      >
        <FontAwesomeIcon icon={faExchange} /> Switch to{' '}
        {monolithUser.canSwitchToOrganisationRole === 'SSO' ? 'SSO' : 'TSO'}{' '}
        Role
      </Button>
    );
  }

  if (monolithUser?.canSwitchToOrganisationRole === 'SHIPPER') {
    otherSettings.push(
      <Button
        onClick={() => {
          safeLocalStorage.setItem('ENFORCE_SHIPPER_ROLE', 'true');
          setLocationHref(PRISMA_CONFIG.angularUrl); // hard reload and redirect to start page
        }}
      >
        <FontAwesomeIcon icon={faExchange} /> Switch to Shipper Role
      </Button>
    );
  }

  if (
    monolithUser?.isOperator &&
    monolithUser.hasShipperRole &&
    monolithUser.hasAwaitingShipperRole
  ) {
    otherSettings.push(
      <Button disabled>Shipper Role Awaiting Activation</Button>
    );
  }

  otherSettings.push(
    <Link to="/logout">
      <FontAwesomeIcon icon={faSignOut} />{' '}
      {accounts.items.length > 1 ? 'Log Out From All Accounts' : 'Log Out'}
    </Link>
  );

  return {
    personalSettings,
    companySettings,
    transportSettings,
    shipperSettings,
    storageSettings,
    otherSettings,
    reportingSettings,
  };
};

const sectionBgColor: Record<UserMenuSection, string | null> = {
  personalSettings: null,
  companySettings: cn('bg-legacy-brand-light-5'),
  transportSettings: cn('legacy:bg-[#ebeffb]'),
  shipperSettings: cn('legacy:bg-[#ebeffb]'),
  storageSettings: cn('legacy:bg-[#ebeffb]'),
  otherSettings: cn('legacy:bg-legacy-brand-light-5'),
  reportingSettings: cn('legacy:bg-[#ebeffb]'),
};

const MenuSection: FC<{
  mode: UserMenuMode;
  section: UserMenuSection;
  settings: ReactNode[];
  title: string | null;
}> = ({ mode, section, settings, title }) => {
  if (!settings.length) return null;

  return (
    <div
      className={cn(
        'not-legacy:grid not-legacy:gap-sm',
        mode === 'dropdown' && sectionBgColor[section]
      )}
      data-testid={section}
    >
      {title && (
        <p
          className={cn(
            `
              not-legacy:text-body-sm not-legacy:font-label-semibold
              not-legacy:text-foreground-surface-subdued
            `,
            mode === 'dropdown' &&
              `
                [font-weight:normal] text-legacy-inactive uppercase
                legacy:p-[0.6rem_2rem] legacy:text-[1rem]
              `,
            mode === 'sidebar-item' &&
              `legacy:my-[0.5rem] legacy:ml-[1rem] legacy:font-headline`,
            mode === 'link-primary' &&
              `
                [font-weight:normal] text-legacy-brand-light-2 uppercase
                legacy:my-[0.5rem] legacy:text-[1rem]
              `
          )}
        >
          {title}
        </p>
      )}

      <div className="not-legacy:grid not-legacy:gap-md">
        {settings.map((setting, index) => (
          <div
            key={index}
            className={`
              grid text-left
              last:border-b-0
              legacy:py-[0.2rem]
            `}
          >
            {setting}
          </div>
        ))}
      </div>
    </div>
  );
};
